import React, { useEffect, useMemo, useState } from 'react';
import { Wrench, BatteryWarning, Hourglass } from 'lucide-react';
import { gradientCssFor, initials } from '../../utils/avatarGradients';
import { vozidloColor } from '../../utils/vozidloColor';

// Práh palubního napětí — sjednoceno s Inspectorem.
const BATTERY_OK_V = 12.0;

const SECTION_COLOR = '#0CC0DF';

/**
 * Stav vozidla podle telemetrie z trackeru (Teltonika) — pouze 3 stavy:
 *   ignition=ON & jede   → driving (Jede,  zelená)
 *   ignition=ON & stojí  → idle    (Stojí, žlutá; motor běží na místě)
 *   ignition=OFF         → parked  (Zaparkováno, šedá; klíč vypnutý)
 *
 * Servis a "neaktivni" stav DB nezobrazujeme samostatně — taková auta
 * budou klasifikovaná jako parked (typicky stojí s vypnutým motorem).
 *
 * Fallback bez ignition signálu: rychlost > 5 km/h = jede, jinak = zaparkováno.
 */
const STATUS_BADGE = {
    driving: { label: 'Jede',         color: '#10B981', bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.30)' },
    idle:    { label: 'Stojí',        color: '#FFC857', bg: 'rgba(255,200,87,0.12)',  border: 'rgba(255,200,87,0.30)' },
    parked:  { label: 'Zaparkováno',  color: '#9CA3AF', bg: 'rgba(156,163,175,0.12)', border: 'rgba(156,163,175,0.30)' },
};

export function classifyVozidlo(v) {
    const rychlost = Number(v?.rychlost_kmh ?? 0);
    const ignition = v?.ignition;
    const motion   = v?.motion;

    if (ignition === true)  return (motion === true || rychlost > 0) ? 'driving' : 'idle';
    if (ignition === false) return 'parked';

    // Fallback pro vozidla bez trackeru / starší záznamy bez ignition pole.
    if (rychlost > 5) return 'driving';
    return 'parked';
}

export default function VozidloDlazdice({ vozidlo, ridic, rozvoz, selected, onClick, onOpenDetail, compact = false, cardStyle, isDark }) {
    // POZOR: VŠECHNY hooky musí být volány vždy ve stejném pořadí, bez ohledu
    // na `compact` mode. Když parent přepne compact (typicky při resize přes
    // 768 px breakpoint), stejná instance pak rendere s jiným počtem hooků
    // a React vyhodí #300 "Rendered fewer hooks than expected". Proto všechny
    // hooky deklarujeme nahoře, AŽ POTOM se větvíme na compact / full.
    const status = useMemo(() => classifyVozidlo(vozidlo), [vozidlo]);

    // Real-time counter "u zákazníka" — relevantní jen pro full kartu, ale
    // useState/useEffect dvojici pouštíme vždy (no-op když compact / bez předání).
    const predaniId = rozvoz?.aktualni_predani?.objednavka_id || null;
    const predaniStartIso = rozvoz?.aktualni_predani?.prijezd_at || null;
    const [, setNowTick] = useState(0);
    useEffect(() => {
        if (!predaniId || compact) return;
        const i = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(i);
    }, [predaniId, compact]);

    const badge = STATUS_BADGE[status];
    const carColorEarly = vozidloColor(vozidlo.id);

    // Kompaktní avatar (mobile overlay nad mapou) — barevný kroužek dle vozidla,
    // pod ním 2 mini-bubliny: rychlost a palivo. Single-click = select, double = detail.
    if (compact) {
        const speedNum = status === 'driving' ? Math.round(vozidlo.rychlost_kmh ?? 0) : 0;
        const palivo = vozidlo.palivo_pct ?? null;
        const labelColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
        const textColor = isDark ? '#fff' : 'var(--text-primary)';
        const chipBg = isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.92)';
        const chipBorder = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
        return (
            <button
                type="button"
                onDoubleClick={onOpenDetail}
                title={`${vozidlo.spz}${ridic ? ` · ${ridic.jmeno || ''} ${ridic.prijmeni || ''}`.trim() : ''} (dvojklik = detail)`}
                style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: 4,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    flexShrink: 0,
                }}
            >
                <div style={{
                    width: 44, height: 44,
                    borderRadius: '50%',
                    padding: 2,
                    background: selected ? SECTION_COLOR : carColorEarly,
                    boxShadow: selected ? `0 0 0 2px ${SECTION_COLOR}` : 'none',
                    transition: 'box-shadow 0.15s',
                }}>
                    <div style={{
                        width: '100%', height: '100%',
                        borderRadius: '50%',
                        background: ridic?.foto_url
                            ? `url(${ridic.foto_url}) center/cover`
                            : gradientCssFor(ridic?.id || vozidlo.aktualni_ridic_id || 0),
                        backgroundPosition: ridic?.foto_url ? (ridic.foto_position || 'center') : 'center',
                        color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700,
                        border: `2px solid ${isDark ? '#0A0612' : '#fff'}`,
                    }}>
                        {!ridic?.foto_url && (ridic ? initials(ridic.jmeno, ridic.prijmeni) : '—')}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 3 }}>
                    <span style={{
                        padding: '1px 6px', borderRadius: 99,
                        background: chipBg, border: `1px solid ${chipBorder}`,
                        fontSize: 9, fontWeight: 700, color: textColor,
                        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                    }} title="Rychlost">
                        {speedNum} <span style={{ color: labelColor, fontWeight: 600 }}>km/h</span>
                    </span>
                    <span style={{
                        padding: '1px 6px', borderRadius: 99,
                        background: chipBg, border: `1px solid ${chipBorder}`,
                        fontSize: 9, fontWeight: 700,
                        color: palivo !== null && palivo < 20 ? '#EF4444' : textColor,
                        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                    }} title="Palivo">
                        {palivo !== null ? Math.round(palivo) : '—'} <span style={{ color: palivo !== null && palivo < 20 ? '#EF4444' : labelColor, fontWeight: 600 }}>%</span>
                    </span>
                </div>
            </button>
        );
    }

    // Metriky podle stavu
    const m1Label = 'Rychlost';
    let m1Value = '—';
    if (status === 'driving') m1Value = `${Math.round(vozidlo.rychlost_kmh ?? 0)} km/h`;
    else if (status === 'idle') m1Value = '0 km/h';
    const palivo = vozidlo.palivo_pct ?? null;
    const m2Value = palivo !== null ? `${Math.round(palivo)} %` : '—';

    // Warning blok
    const stkDni = vozidlo.stk_dni_do_expirace;
    const showStkWarning = stkDni !== null && stkDni !== undefined && stkDni < 30;

    // Driver patička
    const driverName = ridic ? `${ridic.jmeno || ''} ${ridic.prijmeni || ''}`.trim() : null;
    const driverRole = ridic?.role_nazev || ridic?.kariera_uroven_nazev || '';

    // Doba v jízdě
    let driverMeta = '';
    if (vozidlo.zacatek_jizdy_at) {
        const min = Math.max(0, Math.floor((Date.now() - new Date(vozidlo.zacatek_jizdy_at).getTime()) / 60000));
        const h = Math.floor(min / 60);
        const m = min % 60;
        driverMeta = h > 0 ? `${h}:${String(m).padStart(2, '0')} h` : `${m} min`;
    } else if (status === 'parked') {
        driverMeta = 'Připraveno';
    }

    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const dashedBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)';
    const metricBg = isDark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.03)';

    const baseStyle = {
        ...cardStyle,
        borderRadius: 10,
        padding: 14,
        marginBottom: 8,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        position: 'relative',
        transition: 'border-color 0.15s, box-shadow 0.15s',
    };
    const selectedStyle = selected ? {
        borderColor: 'rgba(12,192,223,0.50)',
        boxShadow: '0 0 0 1px rgba(12,192,223,0.30), 0 4px 16px rgba(12,192,223,0.15)',
    } : null;

    const carColor = vozidloColor(vozidlo.id);

    // predaniId / predaniStartIso jsou už spočítané nahoře (před hooky).
    const predaniSec = predaniStartIso
        ? Math.max(0, Math.floor((Date.now() - new Date(predaniStartIso).getTime()) / 1000))
        : null;
    const fmtPredaniDuration = (sec) => {
        if (sec === null) return null;
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    return (
        <button
            type="button"
            onDoubleClick={onOpenDetail}
            title="Dvojklik = otevřít detail"
            style={{ ...baseStyle, ...(selectedStyle || {}) }}
        >
            {/* Per-vozidlo barevný proužek vlevo — stejná barva jako marker na mapě
                (vozidloColor hash). Při výběru ho přebije teal selection bar. */}
            <span style={{
                position: 'absolute',
                left: -1,
                top: 10,
                bottom: 10,
                width: 3,
                background: selected ? SECTION_COLOR : carColor,
                borderRadius: '0 2px 2px 0',
            }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                {/* Levý sloupec: avatar řidiče + jméno pod ním */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0, width: 52 }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: ridic?.foto_url
                            ? `url(${ridic.foto_url}) center/cover`
                            : gradientCssFor(ridic?.id || vozidlo.aktualni_ridic_id || 0),
                        backgroundPosition: ridic?.foto_url ? (ridic.foto_position || 'center') : 'center',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        fontWeight: 700,
                        border: `2px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                    }}>
                        {!ridic?.foto_url && (ridic ? initials(ridic.jmeno, ridic.prijmeni) : '—')}
                    </div>
                    {driverName ? (
                        <div style={{
                            fontSize: 9, fontWeight: 600,
                            color: isDark ? '#fff' : 'var(--text-primary)',
                            textAlign: 'center',
                            lineHeight: 1.2,
                            maxWidth: 52,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {ridic.jmeno_kratke || (ridic.jmeno?.[0] ? `${ridic.jmeno[0]}.${ridic.prijmeni || ''}` : (ridic.cele_jmeno || ridic.jmeno))}
                        </div>
                    ) : (
                        <div style={{ fontSize: 9, color: labelColor, textAlign: 'center' }}>
                            Bez řidiče
                        </div>
                    )}
                </div>

                {/* Pravý sloupec: SPZ + badge, model, metriky */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 6 }}>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontWeight: 800,
                            fontSize: 14,
                            letterSpacing: 0.5,
                            fontVariantNumeric: 'tabular-nums',
                            color: isDark ? '#fff' : 'var(--text-primary)',
                        }}>
                            {vozidlo.spz}
                            {(vozidlo.stav === 'servis' || vozidlo.aktivni_servis) && (
                                <Wrench size={13} style={{ color: '#FF8A00' }} aria-label="V servisu" />
                            )}
                            {vozidlo.power_v !== null && vozidlo.power_v !== undefined && Number(vozidlo.power_v) < BATTERY_OK_V && (
                                <BatteryWarning
                                    size={13}
                                    style={{ color: '#EF4444' }}
                                    aria-label={`Slabá baterie ${Number(vozidlo.power_v).toFixed(1)} V`}
                                />
                            )}
                        </span>
                        <span style={{
                            display: 'inline-flex',
                            padding: '3px 8px',
                            borderRadius: 5,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            textTransform: 'uppercase',
                            background: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            flexShrink: 0,
                        }}>
                            {badge.label}
                        </span>
                    </div>
                    <div style={{ fontSize: 11, color: labelColor, marginBottom: 8 }}>
                        {vozidlo.model}{vozidlo.rok_vyroby ? ` · ${vozidlo.rok_vyroby}` : ''}
                        {driverMeta && (
                            <span style={{ float: 'right', color: subtle, fontVariantNumeric: 'tabular-nums' }}>
                                {driverMeta}
                            </span>
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <div style={{ background: metricBg, borderRadius: 6, padding: '6px 8px' }}>
                            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: labelColor }}>
                                {m1Label}
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: isDark ? '#fff' : 'var(--text-primary)' }}>
                                {m1Value}
                            </div>
                        </div>
                        <div style={{ background: metricBg, borderRadius: 6, padding: '6px 8px' }}>
                            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: labelColor }}>
                                Palivo
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: isDark ? '#fff' : 'var(--text-primary)' }}>
                                {m2Value}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showStkWarning && (
                <div style={{
                    marginTop: 8,
                    padding: '6px 8px',
                    borderRadius: 6,
                    background: 'rgba(255,200,87,0.10)',
                    border: '1px solid rgba(255,200,87,0.25)',
                    color: '#FFC857',
                    fontSize: 11,
                }}>
                    STK končí za {stkDni} dní
                </div>
            )}

            {/* Mini-dlaždice objednávek — doručené + na cestě, číslování stabilní
                přes celou vlnu (backend assigne doručeným 1..K, na cestě K+1..N). */}
            {(() => {
                const dorucene = Array.isArray(rozvoz?.objednavky_dorucene) ? rozvoz.objednavky_dorucene : [];
                const naCeste = Array.isArray(rozvoz?.objednavky_na_ceste) ? rozvoz.objednavky_na_ceste : [];
                const all = [
                    ...dorucene.map((o) => ({ ...o, _kind: 'dorucena' })),
                    ...naCeste.map((o) => ({ ...o, _kind: 'na_ceste' })),
                ].sort((a, b) => (a.poradi_plan || 99) - (b.poradi_plan || 99));
                if (all.length === 0) return null;

                const fmtTime = (iso) => {
                    if (!iso) return '—';
                    try { return new Date(iso).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }); }
                    catch { return '—'; }
                };

                return (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {all.map((o) => {
                            const late = (o.lateness_min || 0) > 0;
                            const dorucena = o._kind === 'dorucena';
                            const aktualniZakaznik = predaniId === o.id;
                            // Pozadí: doručená neutrální, na cestě & late = červená
                            const bg = late && !dorucena
                                ? 'rgba(239,68,68,0.08)'
                                : (isDark ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.025)');
                            const borderCol = late && !dorucena
                                ? 'rgba(239,68,68,0.30)'
                                : (isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)');

                            // Status badge: doručeno → zelená/červená dle lateness;
                            // aktuálně u zákazníka → fialový tikající counter doby čekání;
                            // jinak na cestě → ETA + lateness
                            const renderStatusBadge = () => {
                                if (aktualniZakaznik && predaniSec !== null) {
                                    return (
                                        <span style={{
                                            display: 'inline-flex', flexDirection: 'column',
                                            fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                                        }}>
                                            <span style={{ color: subtle, fontSize: 9 }}>u zákazníka</span>
                                            <span style={{
                                                fontWeight: 700, fontSize: 11,
                                                padding: '1px 6px', borderRadius: 999,
                                                background: 'rgba(151,90,205,0.18)',
                                                color: '#975ACD',
                                                border: '1px solid rgba(151,90,205,0.40)',
                                                whiteSpace: 'nowrap',
                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                            }}>
                                                <Hourglass size={9} /> {fmtPredaniDuration(predaniSec)}
                                            </span>
                                        </span>
                                    );
                                }
                                if (dorucena) {
                                    return (
                                        <span style={{
                                            display: 'inline-flex', flexDirection: 'column',
                                            fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                                        }}>
                                            <span style={{ color: subtle, fontSize: 9 }}>doručeno</span>
                                            <span style={{
                                                fontWeight: 700, fontSize: 11,
                                                padding: '1px 6px', borderRadius: 999,
                                                background: late ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                                                color: late ? '#EF4444' : '#10B981',
                                                border: `1px solid ${late ? 'rgba(239,68,68,0.30)' : 'rgba(16,185,129,0.30)'}`,
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {fmtTime(o.cas_doruceni)}
                                                {late && ` +${o.lateness_min}m`}
                                            </span>
                                        </span>
                                    );
                                }
                                return (
                                    <span style={{
                                        display: 'inline-flex', flexDirection: 'column',
                                        fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                                    }}>
                                        <span style={{ color: subtle, fontSize: 9 }}>odhad</span>
                                        <span style={{
                                            color: late ? '#EF4444' : (isDark ? '#fff' : 'var(--text-primary)'),
                                            fontWeight: 600, fontSize: 11,
                                        }}>
                                            {fmtTime(o.ocekavany_doraz_at)}
                                            {late && ` +${o.lateness_min}m`}
                                        </span>
                                    </span>
                                );
                            };

                            // Aktualni zakaznik — fialové výrazné pozadí, přebije late
                            const finalBg = aktualniZakaznik
                                ? 'rgba(151,90,205,0.12)'
                                : bg;
                            const finalBorder = aktualniZakaznik
                                ? 'rgba(151,90,205,0.45)'
                                : borderCol;

                            return (
                                <div key={o.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '5px 7px', borderRadius: 6,
                                    background: finalBg,
                                    border: `1px solid ${finalBorder}`,
                                    fontSize: 10,
                                    opacity: dorucena ? 0.85 : 1,
                                }}>
                                    {/* Pořadové číslo */}
                                    <span style={{
                                        flexShrink: 0,
                                        width: 18, height: 18, borderRadius: '50%',
                                        background: dorucena ? `${carColor}88` : carColor,
                                        color: '#fff',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: 10,
                                    }}>
                                        {o.poradi_plan ?? '?'}
                                    </span>
                                    {/* Slíbeno + zákazník */}
                                    <span style={{
                                        display: 'inline-flex', flexDirection: 'column',
                                        fontVariantNumeric: 'tabular-nums',
                                        minWidth: 0, flex: 1,
                                    }}>
                                        <span style={{
                                            color: subtle, fontSize: 9,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {o.zakaznik_jmeno || `slíbeno ${fmtTime(o.slibeny_at)}`}
                                        </span>
                                        <span style={{
                                            color: isDark ? '#fff' : 'var(--text-primary)',
                                            fontWeight: 600, fontSize: 11,
                                        }}>
                                            {fmtTime(o.slibeny_at)}
                                        </span>
                                    </span>
                                    {renderStatusBadge()}
                                    {/* Položky · suma */}
                                    <span style={{
                                        textAlign: 'right',
                                        color: subtle, fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                                    }}>
                                        <div style={{ fontSize: 10 }}>
                                            {o.pocet_polozek > 0 ? `${o.pocet_polozek} ks` : '—'}
                                        </div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#fff' : 'var(--text-primary)' }}>
                                            {Math.round(o.celkem_s_dph || 0).toLocaleString('cs-CZ')} Kč
                                        </div>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}
        </button>
    );
}
